import { useState, useEffect } from "react";
import { useToastStore } from "../stores/toastStore";
import {
  isPushSupported,
  getPushSubscription,
  enablePush,
  disablePush,
} from "../utils/push";

type PushState = "off" | "on" | "denied";

interface Props {
  watchlistCodes: string[];
}

/**
 * 관심종목 섹션 헤더 우측에 표시하는 알림 토글 버튼 (Cycle 88).
 * 미지원 브라우저에서는 null 렌더(숨김).
 */
export default function PushToggle({ watchlistCodes }: Props) {
  const { toast } = useToastStore();
  const [pushState, setPushState] = useState<PushState>("off");
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // 마운트 시 현재 구독·권한 상태 파악
  useEffect(() => {
    if (!isPushSupported()) {
      setInitialized(true);
      return;
    }
    const permission = Notification.permission;
    if (permission === "denied") {
      setPushState("denied");
      setInitialized(true);
      return;
    }
    getPushSubscription()
      .then((sub) => {
        if (sub) setPushState("on");
        else setPushState("off");
      })
      .catch(() => {
        setPushState("off");
      })
      .finally(() => setInitialized(true));
  }, []);

  // 미지원 브라우저 또는 초기화 전에는 렌더하지 않음
  if (!isPushSupported() || !initialized) return null;

  const handleToggle = async () => {
    if (loading) return;

    if (pushState === "on") {
      setLoading(true);
      try {
        await disablePush();
        setPushState("off");
        toast.info("알림이 해제되었습니다.");
      } catch {
        toast.error("알림 해제 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // off 상태 → 구독 시도
    setLoading(true);
    const result = await enablePush(watchlistCodes);
    setLoading(false);

    if (result.ok) {
      setPushState("on");
      toast.success("관심종목 점수 변동 알림을 받습니다.");
    } else {
      switch (result.reason) {
        case "denied":
          setPushState("denied");
          toast.error("브라우저 알림 권한이 차단되었습니다.");
          break;
        case "no-vapid":
          toast.error("알림 설정이 준비되지 않았습니다.");
          break;
        case "server":
          toast.error("구독 저장 실패");
          break;
        default:
          break;
      }
    }
  };

  const isDenied = pushState === "denied";
  const isOn = pushState === "on";

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={isDenied || loading}
      aria-pressed={isOn}
      aria-label={
        isDenied
          ? "알림 권한이 차단되어 있습니다"
          : isOn
            ? "알림 끄기"
            : "알림 켜기"
      }
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
        ${
          isDenied
            ? "border-zinc-200 dark:border-zinc-700 text-zinc-400 dark:text-zinc-500 cursor-not-allowed bg-zinc-50 dark:bg-zinc-900"
            : isOn
              ? "border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 cursor-pointer bg-white dark:bg-zinc-900"
              : "border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:border-indigo-300 dark:hover:border-indigo-700 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer bg-white dark:bg-zinc-900"
        }
      `}
    >
      {/* 벨 아이콘 (인라인 SVG) */}
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        {isOn && <line x1="1" y1="1" x2="23" y2="23" />}
      </svg>
      <span>
        {isDenied ? "알림 차단됨" : isOn ? "알림 끄기" : "알림 켜기"}
      </span>
    </button>
  );
}
